import type {
  ApiResponse,
  ApiError,
  Analysis,
  AnalysisDesign,
  DesignFidelity,
  AuthSession,
  ListingDraft,
  UploadedAsset,
  Shop,
  ShopStats,
  AIModels,
  ProductAnalysis,
  DesignVariation,
  ErankListing,
  ErankUploadRow,
  ErankUploadResult,
  ErankShopGroup,
  ErankShopStatsResponse,
  ErankListQuery,
  ErankListResponse,
  ErankTagsResponse,
  CustomMockup,
  MockupCollection,
  MockupFolder,
  SupplierCatalog,
  UpdateCatalogGroupsPayload,
  GeneratedVariationResult,
  ComboVariationResult,
  GenerateComboVariationsPayload,
  VariationTemplate,
  CreateVariationTemplatePayload,
  Design,
  DesignListingLink,
  CreateDesignPayload,
  UpdateDesignPayload,
  DesignStyle,
  DesignStyleItem,
  PersonalizationField,
} from "../types/index";
import type { GenerateVariationsInput } from "../schemas/supplier";
import type { CreateAnalysisInput } from "../schemas/analysis";
import type { CreateListingDraftInput, UpdateListingDraftInput } from "../schemas/listing";
import type { MockLoginInput } from "../schemas/auth";
import type { TrackShopInput } from "../schemas/shop";

/** Siparis tasariminda musteri yazisinin harf harf kontrolu (apps/api/src/lib/text-proof.ts) */
export interface OrderTextProof {
  status: "ok" | "mismatch";
  found: string[];
  fields: Array<{ label: string; value: string; words: Array<{ word: string; status: "ok" | "case" | "missing"; closest?: string }> }>;
}

/** Baskiya hazirlik denetimi (apps/api/src/lib/print-check.ts) */
export interface PrintCheckResult {
  templateKey: string;
  variantId: string;
  effectiveDpi: number;
  width: number;
  height: number;
  issues: Array<{ level: "error" | "warn" | "info"; code: string; message: string }>;
  /** Kumas urunlerde tasarimin her urun renginde gorunen orani (%) */
  colorFit?: Array<{ name: string; hex: string; visible: number }>;
}

export class ApiClientError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly errorCode: string,
    message: string,
    public readonly details?: Record<string, string[]>
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private shopId: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  setToken(token: string | null) {
    this.token = token;
  }

  // Aktif Etsy magazasi — her istege X-Shop-Id olarak eklenir.
  // Bos birakilirsa backend kullanicinin varsayilan magazasini kullanir.
  setShopId(shopId: string | null) {
    this.shopId = shopId;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    if (this.shopId) {
      headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorBody: ApiError;
      try {
        errorBody = (await response.json()) as ApiError;
      } catch {
        errorBody = {
          error: "UNKNOWN_ERROR",
          message: `HTTP ${response.status}`,
          statusCode: response.status,
        };
      }
      throw new ApiClientError(
        response.status,
        errorBody.error,
        errorBody.message,
        errorBody.details
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const body = (await response.json()) as ApiResponse<T>;
    return body.data;
  }

  // Auth
  async login(payload: MockLoginInput): Promise<AuthSession> {
    return this.request<AuthSession>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Backward compat alias
  async mockLogin(payload: MockLoginInput): Promise<AuthSession> {
    return this.login(payload);
  }

  // Health
  async health(): Promise<{ status: string; timestamp: string }> {
    return this.request("/api/health");
  }

  // Analyses
  async createAnalysis(payload: CreateAnalysisInput): Promise<Analysis> {
    return this.request<Analysis>("/api/analyses", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getAnalysis(id: string): Promise<Analysis> {
    return this.request<Analysis>(`/api/analyses/${id}`);
  }

  async listAnalyses(): Promise<Analysis[]> {
    return this.request<Analysis[]>("/api/analyses");
  }

  async regenerateAnalysisListing(id: string, model?: string): Promise<Analysis> {
    return this.request<Analysis>(`/api/analyses/${id}/regenerate-listing`, {
      method: "POST",
      body: JSON.stringify(model ? { model } : {}),
    });
  }

  async deleteAnalysis(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/analyses/${id}`, {
      method: "DELETE",
    });
  }

  // Mevcut analize "custom" etiketi ekle/kaldir
  async setAnalysisCustom(id: string, custom: boolean): Promise<{ custom: boolean }> {
    return this.request<{ custom: boolean }>(`/api/analyses/${id}/custom`, {
      method: "POST",
      body: JSON.stringify({ custom }),
    });
  }

  // Analizin kisisellestirme alanlarini kaydet/duzenle
  async setAnalysisPersonalization(id: string, personalization: PersonalizationField[]): Promise<{ personalization: PersonalizationField[] }> {
    return this.request<{ personalization: PersonalizationField[] }>(`/api/analyses/${id}/personalization`, {
      method: "POST",
      body: JSON.stringify({ personalization }),
    });
  }

  // Siparis tasarimi uret: referansi sadik yeniden uret + personalization degerlerini uygula
  async generateOrderDesign(analysisId: string, payload: {
    values: Array<{ label: string; type?: string; value?: string; imageCount?: number }>;
    referenceImageUrl?: string; customerImages?: string[]; model: string;
  }): Promise<{ id: string; imageUrl?: string; sku?: string; textProof?: OrderTextProof | null }> {
    return this.request<{ id: string; imageUrl?: string; sku?: string; textProof?: OrderTextProof | null }>(`/api/analyses/${analysisId}/order-design`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Tasarim gorseli urun baski sablonuna gore baskiya hazir mi
  async printCheck(payload: { imageUrl: string; templateKey: string; variantId?: string }): Promise<PrintCheckResult> {
    return this.request<PrintCheckResult>(`/api/designs/print-check`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Siparis tasariminda musteri yazisini yeniden kontrol et
  async proofOrderDesign(analysisId: string, payload: {
    imageUrl: string; values: Array<{ label: string; type?: string; value?: string }>;
  }): Promise<{ textProof: OrderTextProof }> {
    return this.request<{ textProof: OrderTextProof }>(`/api/analyses/${analysisId}/order-design/proof`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Analiz tasarım atölyesi
  async getDesignModels(): Promise<Array<{ id: string; name: string; description?: string; price?: string }>> {
    return this.request<Array<{ id: string; name: string; description?: string; price?: string }>>("/api/analyses/design/models");
  }

  async listAnalysisDesigns(analysisId: string): Promise<AnalysisDesign[]> {
    return this.request<AnalysisDesign[]>(`/api/analyses/${analysisId}/designs`);
  }

  async listMyDesigns(): Promise<AnalysisDesign[]> {
    return this.request<AnalysisDesign[]>("/api/analyses/designs/mine");
  }

  async generateAnalysisDesign(
    analysisId: string,
    payload: { prompt: string; model: string; fidelity?: string; referenceDesignId?: string; cleanBackground?: boolean;
      /** Baski alani: tshirt | round | heart | square (yoksa urun tipinden) */
      printShape?: string }
  ): Promise<AnalysisDesign> {
    return this.request<AnalysisDesign>(`/api/analyses/${analysisId}/designs`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async deleteAnalysisDesign(analysisId: string, designId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/analyses/${analysisId}/designs/${designId}`, {
      method: "DELETE",
    });
  }

  // Secili analizler icin toplu tasarim uret (her analiz kendi verisiyle; ortak ayar)
  async bulkGenerateDesigns(payload: {
    analysisIds: string[]; model: string; fidelity?: DesignFidelity; count?: number;
  }): Promise<{ created: number; analyses: number; skipped: number }> {
    return this.request<{ created: number; analyses: number; skipped: number }>("/api/analyses/bulk-designs", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async uploadAnalysisDesign(analysisId: string, imageDataUrl: string): Promise<AnalysisDesign> {
    return this.request<AnalysisDesign>(`/api/analyses/${analysisId}/designs/upload`, {
      method: "POST",
      body: JSON.stringify({ image: imageDataUrl }),
    });
  }

  // Listing Drafts
  async createListingDraft(
    payload: CreateListingDraftInput
  ): Promise<ListingDraft> {
    return this.request<ListingDraft>("/api/listing-drafts", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateListingDraft(
    id: string,
    payload: UpdateListingDraftInput
  ): Promise<ListingDraft> {
    return this.request<ListingDraft>(`/api/listing-drafts/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async getListingDraft(id: string): Promise<ListingDraft> {
    return this.request<ListingDraft>(`/api/listing-drafts/${id}`);
  }

  // Assets
  async uploadAssetPlaceholder(file: File): Promise<UploadedAsset> {
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/assets/upload-placeholder`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(
        response.status,
        errorBody.error,
        errorBody.message
      );
    }

    const body = (await response.json()) as ApiResponse<UploadedAsset>;
    return body.data;
  }

  // Shops
  async trackShop(payload: TrackShopInput): Promise<Shop> {
    return this.request<Shop>("/api/shops", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async listShops(): Promise<Shop[]> {
    return this.request<Shop[]>("/api/shops");
  }

  async getShop(id: string): Promise<Shop> {
    return this.request<Shop>(`/api/shops/${id}`);
  }

  async getShopStats(id: string): Promise<ShopStats> {
    return this.request<ShopStats>(`/api/shops/${id}/stats`);
  }

  async refreshShop(id: string): Promise<Shop> {
    return this.request<Shop>(`/api/shops/${id}/refresh`, {
      method: "POST",
    });
  }

  async deleteShop(id: string): Promise<void> {
    return this.request<void>(`/api/shops/${id}`, {
      method: "DELETE",
    });
  }

  // Listing Builder
  async getBuilderModels(): Promise<AIModels> {
    return this.request<AIModels>("/api/listing-builder/models");
  }

  async createBuilderProject(payload: { title: string; sourceImageUrl: string; sourceListingUrl?: string }): Promise<ListingDraft> {
    return this.request<ListingDraft>("/api/listing-builder", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getBuilderProject(id: string): Promise<ListingDraft> {
    return this.request<ListingDraft>(`/api/listing-builder/${id}`);
  }

  async listBuilderProjects(): Promise<ListingDraft[]> {
    return this.request<ListingDraft[]>("/api/listing-builder");
  }

  async analyzeProduct(id: string, model?: string): Promise<ProductAnalysis> {
    return this.request<ProductAnalysis>(`/api/listing-builder/${id}/analyze`, {
      method: "POST",
      body: JSON.stringify({ model }),
    });
  }

  async createVariation(id: string, payload: { prompt: string; model: string }): Promise<DesignVariation> {
    return this.request<DesignVariation>(`/api/listing-builder/${id}/variations`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async createBatchVariations(
    id: string,
    payload: { categories: string[]; model?: string; models?: string[]; analysisContext?: string; variationsPerCategory?: number }
  ): Promise<DesignVariation[]> {
    return this.request<DesignVariation[]>(`/api/listing-builder/${id}/batch-variations`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getVariation(id: string, variationId: string): Promise<DesignVariation> {
    return this.request<DesignVariation>(`/api/listing-builder/${id}/variations/${variationId}`);
  }

  async deleteVariation(id: string, variationId: string): Promise<void> {
    return this.request<void>(`/api/listing-builder/${id}/variations/${variationId}`, { method: "DELETE" });
  }

  async deleteBuilderProject(id: string): Promise<void> {
    return this.request<void>(`/api/listing-builder/${id}`, { method: "DELETE" });
  }

  // Settings
  async getSettings(): Promise<Record<string, string>> {
    return this.request<Record<string, string>>("/api/settings");
  }

  async updateSettings(settings: Record<string, string>): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>("/api/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  // Mockups
  async uploadMockup(
    file: File,
    label?: string,
    category?: string,
    folderId?: string | null,
    /** Taki hattinda "concept": sahne sablonu; tekstilde "mockup" */
    kind?: "mockup" | "concept",
  ): Promise<CustomMockup> {
    const formData = new FormData();
    formData.append("file", file);
    if (label) formData.append("label", label);
    if (category) formData.append("category", category);
    if (folderId) formData.append("folderId", folderId);
    if (kind) formData.append("kind", kind);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/mockups/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<CustomMockup>;
    return body.data;
  }

  /** kind: "mockup" = bos urun mockuplari (tekstil/hediyelik),
   *  "concept" = konsept fotograflari (taki). Verilmezse hepsi doner. */
  async listMockups(kind?: "mockup" | "concept" | "chart"): Promise<CustomMockup[]> {
    return this.request<CustomMockup[]>(`/api/mockups${kind ? `?kind=${kind}` : ""}`);
  }

  // Eksik mockup onizlemelerini (thumbnail) uret — picker hizi icin (tek seferlik)
  async backfillMockupThumbnails(): Promise<{ total: number; updated: number }> {
    return this.request<{ total: number; updated: number }>("/api/mockups/backfill-thumbnails", { method: "POST" });
  }

  // Mockup kaynak listeleri (eklenti "Mockup Cek")
  async listMockupSources(): Promise<Array<{ id: string; sourceUrl: string | null; sourceTitle: string | null; imageUrls: string[]; createdAt: string }>> {
    return this.request("/api/mockups/sources");
  }
  async deleteMockupSource(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/mockups/sources/${id}`, { method: "DELETE" });
  }
  // Secilen mockuplardan AI ile bos mockup uret (havuza kaydeder)
  async generateBlankMockups(payload: {
    imageUrls: string[]; model: string; keepShirt?: boolean; cleanBackground?: boolean;
    onlyDesign?: boolean; extraPrompt?: string; folderId?: string;
    /** Uretilen mockup'lara verilecek ad (birden fazlaysa sonuna sira eklenir) */
    label?: string;
  }): Promise<{ results: Array<{ source: string; imageUrl?: string; mockupId?: string; error?: string }>; created: number }> {
    return this.request("/api/mockups/generate-blank", { method: "POST", body: JSON.stringify(payload) });
  }

  async updateMockup(id: string, data: Partial<Pick<CustomMockup, "label" | "category" | "theme" | "opacity" | "designAreaX" | "designAreaY" | "designAreaW" | "designAreaH" | "designAreaRotation" | "designAreas" | "shading" | "displace" | "folderId" | "brand" | "model" | "color" | "colorLabel" | "productTypeKey" | "isSelling" | "bigPrint">>): Promise<CustomMockup> {
    return this.request<CustomMockup>(`/api/mockups/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  /** Occlusion maskesini kaydet ve on plan (foreground) PNG'sini urettir */
  /** Mockup'in big print (16 inc) kopyasi — ayni fotograf, alan 16 inc'e genisler */
  async createBigPrintCopy(id: string): Promise<CustomMockup> {
    return this.request<CustomMockup>(`/api/mockups/${id}/big-print-copy`, { method: "POST" });
  }

  /** Editor onizlemesi: kaydetmeden isik/kirisik haritalari (data URL, 1024px) */
  async mockupPreviewMaps(id: string, body: { areas: unknown[]; shading: number; displace: number; mask?: string }): Promise<{ shade: string | null; disp: string | null; noise: number }> {
    return this.request(`/api/mockups/${id}/preview-maps`, { method: "POST", body: JSON.stringify(body) });
  }

  /** Alanin kendi urun yuzeyi maskesini yukle (beyaz = yuzey) -> url */
  async uploadAreaSurface(id: string, mask: string, area?: { x: number; y: number; w: number; h: number }): Promise<{ url: string }> {
    return this.request(`/api/mockups/${id}/area-surface`, { method: "POST", body: JSON.stringify({ mask, area }) });
  }

  /** Mockup'in kayitli yuzey maskelerini onarir (dikdortgen delik / yanlis kirpma); guncel alanlari dondurur */
  async healMockupSurfaces(id: string): Promise<{ designAreas: unknown[]; healed: number }> {
    return this.request(`/api/mockups/${id}/heal-surfaces`, { method: "POST", body: "{}" });
  }

  /** Derinlik haritasini yukle (tarayicida uretilir; gri PNG, acik = yakin) */
  async saveMockupDepth(id: string, depth: string): Promise<CustomMockup> {
    return this.request<CustomMockup>(`/api/mockups/${id}/depth`, { method: "POST", body: JSON.stringify({ depth }) });
  }

  async saveMockupOcclusion(id: string, mask: string, feather: number): Promise<CustomMockup> {
    return this.request<CustomMockup>(`/api/mockups/${id}/occlusion`, {
      method: "POST",
      body: JSON.stringify({ mask, feather }),
    });
  }

  /** "AI ile kur": bos mockuptaki urunlerin kutulari (piksel). Bos liste =
   *  bulunamadi ya da OpenRouter anahtari yok (reason: "no_key"). */
  /** describe: giyim mockup analizi de ayni AI cagrisindan (analysis olarak doner, taslak kaydedilir) */
  async detectMockupProducts(id: string, opts: { describe?: boolean; model?: string } = {}): Promise<{ boxes: Array<{ x: number; y: number; w: number; h: number; surface?: "flat" | "cylinder" | "tilted" | "garment"; print?: { x: number; y: number; w: number; h: number } }>; occluders?: Array<{ x: number; y: number; w: number; h: number }>; reason?: string; analysis?: import("../types/custom-study").MockupAnalysis }> {
    return this.request(`/api/mockups/${id}/detect-products`, { method: "POST", body: JSON.stringify({ describe: opts.describe === true, ...(opts.model ? { model: opts.model } : {}) }) });
  }

  /** On plan katmanini kaldir */
  async clearMockupOcclusion(id: string): Promise<CustomMockup> {
    return this.request<CustomMockup>(`/api/mockups/${id}/occlusion`, { method: "DELETE" });
  }

  // Secili mockuplara marka/model/renk ata (elle, toplu)
  async bulkAssignMockupVariation(ids: string[], data: { brand?: string | null; model?: string | null; color?: string | null }): Promise<{ updated: number }> {
    return this.request<{ updated: number }>("/api/mockups/bulk-variation", {
      method: "PATCH",
      body: JSON.stringify({ ids, ...data }),
    });
  }

  // Mevcut mockuplardan tema varyantı üret (tişört kilitli, arka plan/tema değişir)
  async generateThemeVariants(payload: { items: Array<{ mockupId: string; colorName?: string }>; theme: string; model: string }): Promise<{ results: Array<{ mockupId: string; newMockupId?: string; imageUrl?: string; error?: string }>; created: number }> {
    return this.request("/api/mockups/theme-variants", { method: "POST", body: JSON.stringify(payload) });
  }

  // ---- Mockup Folders (independent file-system tree) ----
  async listMockupFolders(kind?: "mockup" | "concept"): Promise<MockupFolder[]> {
    return this.request<MockupFolder[]>(`/api/mockups/folders${kind ? `?kind=${kind}` : ""}`);
  }

  async createMockupFolder(name: string, parentId?: string | null, kind?: "mockup" | "concept"): Promise<MockupFolder> {
    return this.request<MockupFolder>("/api/mockups/folders", {
      method: "POST",
      body: JSON.stringify({ name, parentId, kind }),
    });
  }

  async updateMockupFolder(id: string, data: { name?: string; parentId?: string | null }): Promise<MockupFolder> {
    return this.request<MockupFolder>(`/api/mockups/folders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteMockupFolder(id: string): Promise<void> {
    return this.request<void>(`/api/mockups/folders/${id}`, { method: "DELETE" });
  }

  async deleteMockup(id: string): Promise<void> {
    return this.request<void>(`/api/mockups/${id}`, { method: "DELETE" });
  }

  // Mockup Collections
  async createMockupCollection(name: string, mockupIds?: string[], parentId?: string | null, kind?: "mockup" | "concept"): Promise<MockupCollection> {
    return this.request<MockupCollection>("/api/mockups/collections", {
      method: "POST",
      body: JSON.stringify({ name, mockupIds, parentId, kind }),
    });
  }

  async listMockupCollections(kind?: "mockup" | "concept"): Promise<MockupCollection[]> {
    return this.request<MockupCollection[]>(`/api/mockups/collections${kind ? `?kind=${kind}` : ""}`);
  }

  async updateMockupCollection(id: string, data: { name?: string; mockupIds?: string[]; parentId?: string | null }): Promise<MockupCollection> {
    return this.request<MockupCollection>(`/api/mockups/collections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteMockupCollection(id: string): Promise<void> {
    return this.request<void>(`/api/mockups/collections/${id}`, { method: "DELETE" });
  }

  // Canva
  async getCanvaStatus(): Promise<{ connected: boolean }> {
    return this.request<{ connected: boolean }>("/api/canva/status");
  }

  async connectCanva(): Promise<{ url: string }> {
    return this.request<{ url: string }>("/api/canva/oauth/connect");
  }

  async canvaCallback(code: string, state: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>("/api/canva/oauth/callback", {
      method: "POST",
      body: JSON.stringify({ code, state }),
    });
  }

  async createCanvaDesign(imageUrl: string, title?: string): Promise<{ designId: string; editUrl: string; viewUrl: string }> {
    return this.request<{ designId: string; editUrl: string; viewUrl: string }>("/api/canva/create-design", {
      method: "POST",
      body: JSON.stringify({ imageUrl, title }),
    });
  }

  async exportCanvaDesign(designId: string): Promise<{ urls: string[]; status: string }> {
    return this.request<{ urls: string[]; status: string }>("/api/canva/export-design", {
      method: "POST",
      body: JSON.stringify({ designId }),
    });
  }

  async disconnectCanva(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>("/api/canva/disconnect", { method: "POST" });
  }

  // Supplier Catalogs
  /** Bos katalog olustur — eklentiden aktarim yapmadan once hedefi hazirlamak icin */
  async createSupplierCatalog(payload: {
    name: string; vertical?: string; supplier?: string; description?: string; categories?: unknown[];
  }): Promise<SupplierCatalog> {
    return this.request<SupplierCatalog>("/api/supplier-catalogs", {
      method: "POST",
      body: JSON.stringify({ categories: [], ...payload }),
    });
  }

  /** vertical verilirse yalnizca o dikeyin kataloglari doner */
  async listSupplierCatalogs(vertical?: string): Promise<SupplierCatalog[]> {
    return this.request<SupplierCatalog[]>(`/api/supplier-catalogs${vertical ? `?vertical=${vertical}` : ""}`);
  }

  async getSupplierCatalog(id: string): Promise<SupplierCatalog> {
    return this.request<SupplierCatalog>(`/api/supplier-catalogs/${id}`);
  }

  async importSupplierCatalogExcel(file: File, name: string, supplier?: string, description?: string): Promise<SupplierCatalog> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    if (supplier) formData.append("supplier", supplier);
    if (description) formData.append("description", description);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/supplier-catalogs/import-excel`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<SupplierCatalog>;
    return body.data;
  }

  async reimportSupplierCatalogExcel(id: string, file: File): Promise<SupplierCatalog> {
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/supplier-catalogs/${id}/reimport`, {
      method: "PATCH",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<SupplierCatalog>;
    return body.data;
  }

  async updateSupplierCatalog(id: string, data: Partial<Pick<SupplierCatalog, "name" | "supplier" | "description">>): Promise<SupplierCatalog> {
    return this.request<SupplierCatalog>(`/api/supplier-catalogs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  /** Kategorileri gruplara tasi / grup ekle, sil, yeniden adlandir */
  async updateSupplierCatalogGroups(id: string, payload: UpdateCatalogGroupsPayload): Promise<SupplierCatalog> {
    return this.request<SupplierCatalog>(`/api/supplier-catalogs/${id}/groups`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  /** Kategorilerin SABIT satis fiyatlarini kaydet. Kaydedince ayni kataloga
   *  bagli combo varyasyon sablonlarindaki fiyatlar da sunucuda tazelenir. */
  async updateSupplierCatalogPrices(
    id: string,
    updates: Array<{ categoryId: string; prices: Array<{ size: string; retailPrice: number | null }> }>,
  ): Promise<{ catalog: SupplierCatalog; syncedTemplates: number; syncedTemplateNames: string[] }> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    const res = await fetch(`${this.baseUrl}/api/supplier-catalogs/${id}/prices`, {
      method: "PATCH", headers, body: JSON.stringify({ updates }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiClientError(res.status, body?.error ?? "UNKNOWN_ERROR", body?.message ?? `HTTP ${res.status}`);
    return {
      catalog: body.data as SupplierCatalog,
      syncedTemplates: Number(body.syncedTemplates ?? 0),
      syncedTemplateNames: (body.syncedTemplateNames ?? []) as string[],
    };
  }

  /** Sablonun beden fiyatlarini kaynak katalogun sabit fiyatlariyla tazele */
  async syncTemplatePrices(templateId: string): Promise<unknown> {
    return this.request(`/api/variation-templates/${templateId}/sync-prices`, { method: "POST" });
  }

  async deleteSupplierCatalog(id: string): Promise<void> {
    return this.request<void>(`/api/supplier-catalogs/${id}`, { method: "DELETE" });
  }

  async generateVariationsFromCatalog(payload: GenerateVariationsInput): Promise<GeneratedVariationResult> {
    return this.request<GeneratedVariationResult>("/api/supplier-catalogs/generate-variations", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async generateComboVariations(payload: GenerateComboVariationsPayload): Promise<ComboVariationResult> {
    return this.request<ComboVariationResult>("/api/supplier-catalogs/generate-combo-variations", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async previewSupplierExcel(file: File): Promise<{ categories: any[]; summary: any[] }> {
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/supplier-catalogs/preview-excel`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<{ categories: any[]; summary: any[] }>;
    return body.data;
  }

  // ─── Variation Templates ─────────────────────────────
  async listVariationTemplates(): Promise<VariationTemplate[]> {
    return this.request<VariationTemplate[]>("/api/variation-templates");
  }

  async createVariationTemplate(payload: CreateVariationTemplatePayload): Promise<VariationTemplate> {
    return this.request<VariationTemplate>("/api/variation-templates", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateVariationTemplate(id: string, payload: Partial<CreateVariationTemplatePayload>): Promise<VariationTemplate> {
    return this.request<VariationTemplate>(`/api/variation-templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async deleteVariationTemplate(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/variation-templates/${id}`, {
      method: "DELETE",
    });
  }

  // ─── Designs (Tasarim Havuzu) ─────────────────────────
  async uploadDesign(file: File, payload?: CreateDesignPayload): Promise<Design> {
    const formData = new FormData();
    formData.append("file", file);
    if (payload?.name) formData.append("name", payload.name);
    if (payload?.sku) formData.append("sku", payload.sku);
    if (payload?.tags) formData.append("tags", JSON.stringify(payload.tags));
    if (payload?.source) formData.append("source", payload.source);
    if (payload?.sourceId) formData.append("sourceId", payload.sourceId);
    if (payload?.metadata) formData.append("metadata", JSON.stringify(payload.metadata));

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/designs/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<Design>;
    return body.data;
  }

  async saveDesignFromUrl(url: string, payload?: CreateDesignPayload): Promise<Design> {
    return this.request<Design>("/api/designs/from-url", {
      method: "POST",
      body: JSON.stringify({ url, ...payload }),
    });
  }

  async listDesigns(params?: {
    search?: string;
    tags?: string;
    sku?: string;
    source?: string;
    sort?: string;
    page?: number;
    limit?: number;
    noListing?: boolean;
    copyright?: string;
    custom?: boolean;
    /** Kategori filtresi: tek urun tipi */
    productTypeKey?: string;
    /** Kategori filtresi: dikeydeki tum urun tipleri (magazanin kategorisi) */
    vertical?: string;
    /** ChatGPT (MCP) ile olusturulan tasarimlar: only = yalniz onlar, hide = gizle */
    chatgpt?: "only" | "hide";
  }): Promise<{ data: Design[]; total: number; page: number; pageSize: number; hasMore: boolean }> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.tags) qs.set("tags", params.tags);
    if (params?.sku) qs.set("sku", params.sku);
    if (params?.source) qs.set("source", params.source);
    if (params?.sort) qs.set("sort", params.sort);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.noListing) qs.set("noListing", "1");
    if (params?.chatgpt) qs.set("chatgpt", params.chatgpt);
    if (params?.productTypeKey) qs.set("productTypeKey", params.productTypeKey);
    if (params?.vertical) qs.set("vertical", params.vertical);
    if (params?.copyright) qs.set("copyright", params.copyright);
    if (params?.custom) qs.set("custom", "1");
    const query = qs.toString();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/designs${query ? `?${query}` : ""}`, {
      headers,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    return (await response.json()) as { data: Design[]; total: number; page: number; pageSize: number; hasMore: boolean };
  }

  /** Analiz tasarimi id -> havuzdaki (Tasarimlar) tasarim id + koyu varyant */
  async getAnalysisDesignPoolMap(analysisId: string): Promise<Record<string, { variantUrl: string | null; theme: string; designId: string }>> {
    return this.request(`/api/analyses/${analysisId}/customization/variants`);
  }

  async getDesign(id: string): Promise<Design> {
    return this.request<Design>(`/api/designs/${id}`);
  }

  async updateDesign(id: string, payload: UpdateDesignPayload): Promise<Design> {
    return this.request<Design>(`/api/designs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  // Ilham alinan analizi degistir/ekle (null => kaldir)
  async setDesignSourceAnalysis(id: string, analysisId: string | null): Promise<Design> {
    return this.request<Design>(`/api/designs/${id}/source-analysis`, {
      method: "PATCH",
      body: JSON.stringify({ analysisId }),
    });
  }

  async setDesignThemeVariant(
    id: string,
    payload: { theme?: "light" | "dark"; variantImage?: string; removeVariant?: boolean }
  ): Promise<Design> {
    return this.request<Design>(`/api/designs/${id}/theme-variant`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async uploadEditedDesign(id: string, file: File): Promise<Design> {
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
      // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
      if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    }

    const response = await fetch(`${this.baseUrl}/api/designs/${id}/edit`, {
      method: "PATCH",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, errorBody.error, errorBody.message);
    }

    const body = (await response.json()) as ApiResponse<Design>;
    return body.data;
  }

  async revertDesign(id: string): Promise<Design> {
    return this.request<Design>(`/api/designs/${id}/revert`, {
      method: "POST",
    });
  }

  async deleteDesign(id: string): Promise<void> {
    return this.request<void>(`/api/designs/${id}`, { method: "DELETE" });
  }

  async linkDesignToListing(id: string, listingDraftId: string, imageIndex?: number): Promise<DesignListingLink> {
    return this.request<DesignListingLink>(`/api/designs/${id}/link`, {
      method: "POST",
      body: JSON.stringify({ listingDraftId, imageIndex }),
    });
  }

  async unlinkDesignFromListing(id: string, listingDraftId: string): Promise<void> {
    return this.request<void>(`/api/designs/${id}/link/${listingDraftId}`, { method: "DELETE" });
  }

  async bulkDeleteDesigns(ids: string[]): Promise<{ deleted: number }> {
    return this.request<{ deleted: number }>("/api/designs/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  }

  async bulkTagDesigns(ids: string[], tags: string[]): Promise<{ updated: number }> {
    return this.request<{ updated: number }>("/api/designs/bulk-tag", {
      method: "POST",
      body: JSON.stringify({ ids, tags }),
    });
  }

  // ─── Design AI (Lifestyle) ─────────────────────────
  async analyzeDesignImage(imageUrl: string, model?: string, productContext?: {
    productTypes?: string[];
    printAreas?: string[];
    colors?: string[];
    hasLightDesign?: boolean;
    hasDarkDesign?: boolean;
  }): Promise<{ analysis: string; keywords: string[]; targetAudience: string; lifestyleScenes: string[] }> {
    return this.request<{ analysis: string; keywords: string[]; targetAudience: string; lifestyleScenes: string[] }>("/api/designs/analyze-image", {
      method: "POST",
      body: JSON.stringify({ imageUrl, model, productContext }),
    });
  }

  async generateLifestyleImage(imageUrl: string, scene: string, model?: string, productContext?: {
    productTypes?: string[];
    colors?: string[];
  }): Promise<{ imageUrl?: string; response: string }> {
    return this.request<{ imageUrl?: string; response: string }>("/api/designs/generate-lifestyle", {
      method: "POST",
      body: JSON.stringify({ imageUrl, scene, model, productContext }),
    });
  }

  async getDesignAIModels(): Promise<{ vision: Array<{ id: string; name: string; price?: string }>; imageGen: Array<{ id: string; name: string; price?: string }> }> {
    return this.request<{ vision: Array<{ id: string; name: string; price?: string }>; imageGen: Array<{ id: string; name: string; price?: string }> }>("/api/designs/ai-models");
  }

  // ─── Tasarım ↔ Listing tutarlılık denetimi ────────────
  async checkDesignConsistency(payload: {
    imageUrl: string; title?: string; description?: string; tags?: string[];
    targetProductType?: string; inspirationTitle?: string; inspirationTags?: string[]; model?: string;
  }): Promise<{
    visibleText: string; technique: string; isEmbroideryLook: boolean; subjects: string[]; inspirationIsDigital: boolean;
    warnings: Array<{ category: "technique" | "digital" | "text"; term?: string; message: string; severity: "high" | "medium" | "low" }>;
  }> {
    return this.request("/api/designs/consistency-check", { method: "POST", body: JSON.stringify(payload) });
  }

  async generateSeoFromDesign(payload: {
    design: { visibleText?: string; subjects?: string[]; technique?: string };
    inspiration?: { title?: string; description?: string; tags?: string[] };
    targetProductType: string; niche?: string; avoidTerms?: string[]; inspirationIsDigital?: boolean; model?: string;
    /** Secili liste sablonu: urunun marka/modeli prompt'a eklenir */
    templateId?: string;
  }): Promise<{ title: string; description: string; tags: string[] }> {
    return this.request("/api/listing-builder/seo-from-design", { method: "POST", body: JSON.stringify(payload) });
  }

  // ─── Tasarım Stilleri (Design Styles) ─────────────────
  async listDesignStyles(): Promise<DesignStyle[]> {
    return this.request<DesignStyle[]>("/api/design-styles");
  }

  async getDesignStyle(id: string): Promise<DesignStyle> {
    return this.request<DesignStyle>(`/api/design-styles/${id}`);
  }

  // images: data URL veya http URL listesi (birden fazla ornek -> daha net stil)
  async createDesignStyle(payload: { name: string; images: string[]; model?: string }): Promise<DesignStyle> {
    return this.request<DesignStyle>("/api/design-styles", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async reanalyzeDesignStyle(id: string, model?: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/api/design-styles/${id}/reanalyze`, {
      method: "POST",
      body: JSON.stringify({ model }),
    });
  }

  async deleteDesignStyle(id: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/api/design-styles/${id}`, { method: "DELETE" });
  }

  // Secili stille yeni tasarim uret (kelime ve/veya konu gorseli)
  async generateStyledDesign(id: string, payload: { subjectPrompt?: string; subjectImage?: string; model: string }): Promise<DesignStyleItem> {
    return this.request<DesignStyleItem>(`/api/design-styles/${id}/generate`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async deleteStyleItem(styleId: string, itemId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/api/design-styles/${styleId}/items/${itemId}`, { method: "DELETE" });
  }

  // ─── Media Generation ─────────────────────────────────

  async generateSlideshowVideo(req: {
    imageUrls: string[];
    aspectRatio?: "1:1" | "16:9" | "9:16";
    duration?: 6 | 8;
    designId?: string;
    zoomPercent?: number;
  }): Promise<{ id: string; videoUrl: string; durationMs: number; width: number; height: number; cached: boolean }> {
    return this.request("/api/media/generate-video", {
      method: "POST",
      body: JSON.stringify(req),
    });
  }

  async getVideoStatus(id: string): Promise<{ id: string; status: string; videoUrl?: string; errorMessage?: string }> {
    return this.request(`/api/media/video-status/${id}`);
  }

  async getMediaHistory(params?: {
    type?: string;
    designId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: unknown[]; total: number; page: number; pageSize: number; hasMore: boolean }> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.designId) qs.set("designId", params.designId);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return this.request(`/api/media/history${query ? `?${query}` : ""}`);
  }

  async getSceneFamilies(): Promise<Array<{ id: string; name: string; description: string; moods: string[]; styleFeel: string[]; matchTags: string[] }>> {
    return this.request("/api/media/scene-families");
  }

  // ===== eRank CSV Analiz =====
  async uploadErankListings(shopName: string, rows: ErankUploadRow[]): Promise<ErankUploadResult> {
    return this.request<ErankUploadResult>("/api/erank/upload", {
      method: "POST",
      body: JSON.stringify({ shopName, rows }),
    });
  }

  async getErankByListing(listingId: string): Promise<ErankListing | null> {
    return this.request<ErankListing | null>(`/api/erank/by-listing/${encodeURIComponent(listingId)}`);
  }

  async getErankByListings(listingIds: string[]): Promise<Record<string, ErankListing>> {
    if (listingIds.length === 0) return {};
    return this.request<Record<string, ErankListing>>("/api/erank/by-listings", {
      method: "POST",
      body: JSON.stringify({ listingIds }),
    });
  }

  async getErankShops(): Promise<ErankShopGroup[]> {
    return this.request<ErankShopGroup[]>("/api/erank/shops");
  }

  async getErankShopStats(windowDays = 7): Promise<ErankShopStatsResponse> {
    return this.request<ErankShopStatsResponse>(`/api/erank/shop-stats?window=${windowDays}`);
  }

  async getErankAiInsight(windowDays = 7, model?: string): Promise<{ insight: string; windowDays: number; shopCount: number }> {
    return this.request<{ insight: string; windowDays: number; shopCount: number }>("/api/erank/ai-insight", {
      method: "POST",
      body: JSON.stringify({ window: windowDays, ...(model ? { model } : {}) }),
    });
  }

  async getErankListings(query: ErankListQuery = {}): Promise<ErankListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
    // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
    if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    const q = qs.toString();
    const response = await fetch(`${this.baseUrl}/api/erank${q ? `?${q}` : ""}`, { headers });
    if (!response.ok) {
      const e = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, e.error, e.message);
    }
    return (await response.json()) as ErankListResponse;
  }

  async getErankTags(query: ErankListQuery & { sort?: string; limit?: number } = {}): Promise<ErankTagsResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    // Aktif magaza: sunucu kayitlari magazaya gore suzuyor; basliksiz istek
    // varsayilan magazaya yaziyor ve kayit digerinde gorunmuyordu.
    if (this.shopId) headers["X-Shop-Id"] = this.shopId;
    const q = qs.toString();
    const response = await fetch(`${this.baseUrl}/api/erank/tags${q ? `?${q}` : ""}`, { headers });
    if (!response.ok) {
      const e = (await response.json()) as ApiError;
      throw new ApiClientError(response.status, e.error, e.message);
    }
    return (await response.json()) as ErankTagsResponse;
  }

  async deleteErankListings(shop?: string): Promise<{ deleted: number }> {
    return this.request<{ deleted: number }>(`/api/erank${shop ? `?shop=${encodeURIComponent(shop)}` : ""}`, {
      method: "DELETE",
    });
  }

  async setErankFavorite(id: string, favorite: boolean): Promise<{ favorite: boolean }> {
    return this.request<{ favorite: boolean }>(`/api/erank/${id}/favorite`, {
      method: "POST",
      body: JSON.stringify({ favorite }),
    });
  }

  /** Filtreye uyan kayitlar (toplu secim icin, en fazla 1000) */
  async getErankIds(query: ErankListQuery = {}): Promise<{ rows: { id: string; listingId: string; title: string }[]; total: number }> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    return this.request<{ rows: { id: string; listingId: string; title: string }[]; total: number }>(`/api/erank/ids?${qs.toString()}`);
  }

  /** Secili (ids) veya filtreye uyan eRank kayitlarini Etsy Open API'den tazeler */
  async refreshErankFromEtsy(body: { ids?: string[]; filters?: Record<string, unknown>; sort?: string; order?: string; limit?: number; force?: boolean }): Promise<{ requested: number; updated: number; removed: number; skipped: number; calls: number }> {
    return this.request<{ requested: number; updated: number; removed: number; skipped: number; calls: number }>("/api/erank/etsy-refresh", { method: "POST", body: JSON.stringify(body) });
  }

  /** Secili eRank kayitlarina toplu islem */
  async erankBulk(ids: string[], action: "favorite" | "unfavorite" | "viewed" | "unviewed" | "delete"): Promise<{ action: string; count: number }> {
    return this.request<{ action: string; count: number }>("/api/erank/bulk", { method: "POST", body: JSON.stringify({ ids, action }) });
  }

  async markErankViewed(id: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/api/erank/${id}/viewed`, { method: "POST", body: "{}" });
  }

  // listingId'lerden hangileri analiz edilmis? -> { [listingId]: analysisId }
  async lookupAnalysesBulk(listingIds: string[]): Promise<Record<string, string>> {
    if (listingIds.length === 0) return {};
    return this.request<Record<string, string>>("/api/analyses/lookup-bulk", {
      method: "POST",
      body: JSON.stringify({ listingIds }),
    });
  }

  // analiz id + telif durumu -> { [listingId]: { analysisId, copyright } }
  async lookupAnalysesBulkMeta(listingIds: string[]): Promise<Record<string, { analysisId: string; copyright: string | null }>> {
    if (listingIds.length === 0) return {};
    return this.request<Record<string, { analysisId: string; copyright: string | null }>>("/api/analyses/lookup-bulk-meta", {
      method: "POST",
      body: JSON.stringify({ listingIds }),
    });
  }
}
